import React, { useState, useEffect, useCallback } from 'react';
import { whatsappTemplatesAPI, whatsappEnviosAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import '../Clientes.css';

const EnviosWhatsApp = () => {
  const { formatFechaHora } = useZonaHoraria();
  const [plantillas, setPlantillas] = useState([]);
  const [envios, setEnvios] = useState([]);
  const [loadingPlantillas, setLoadingPlantillas] = useState(true);
  const [loadingEnvios, setLoadingEnvios] = useState(true);
  const [filtroClienteId, setFiltroClienteId] = useState('');
  const [limite, setLimite] = useState(50);
  const { hasPermission } = useAuth();

  const loadPlantillas = useCallback(async () => {
    try {
      setLoadingPlantillas(true);
      const res = await whatsappTemplatesAPI.getAll();
      if (res.success && Array.isArray(res.data)) setPlantillas(res.data);
      else setPlantillas([]);
    } catch (e) {
      setPlantillas([]);
      toast.error(e.message || 'Error al cargar plantillas');
    } finally {
      setLoadingPlantillas(false);
    }
  }, []);

  const loadEnvios = useCallback(async () => {
    try {
      setLoadingEnvios(true);
      const params = { limite };
      if (filtroClienteId) params.cliente_id = filtroClienteId;
      const res = await whatsappEnviosAPI.getAll(params);
      if (res.success && Array.isArray(res.data)) setEnvios(res.data);
      else setEnvios([]);
    } catch (e) {
      setEnvios([]);
      toast.error('Error al cargar historial');
    } finally {
      setLoadingEnvios(false);
    }
  }, [limite, filtroClienteId]);

  useEffect(() => {
    loadPlantillas();
  }, [loadPlantillas]);

  useEffect(() => {
    loadEnvios();
  }, [loadEnvios]);

  const formatFecha = (f) => {
    if (!f) return '-';
    return formatFechaHora(f);
  };

  if (!hasPermission('ver_clientes')) {
    return <div className="clientes"><p>No tiene permiso para ver esta sección.</p></div>;
  }

  return (
    <div className="clientes pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>WhatsApp Business – Plantillas y envíos</h1>
      </div>

      <div className="listado-panel clientes-listado listado-controles">
        <div className="listado-panel-cuerpo clientes-listado-cuerpo">
          <div className="listado-tabla-area clientes-listado-tabla-area">
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ marginBottom: 12 }}>Plantillas guardadas</h2>
        <p style={{ color: '#666', marginBottom: 12 }}>
          Las plantillas de tipo <strong>Cliente</strong> se usan en envío individual; las de tipo <strong>Grupo</strong> en envío por grupo consolidado. Puede crearlas desde los modales de envío en Clientes.
        </p>
        {loadingPlantillas ? (
          <p>Cargando plantillas...</p>
        ) : plantillas.length === 0 ? (
          <p>No hay plantillas. Cree una desde Clientes → Enviar resumen por WhatsApp → Guardar como nueva.</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Texto (inicio)</th>
                  <th>Imagen</th>
                  <th>Actualizado</th>
                </tr>
              </thead>
              <tbody>
                {plantillas.map(p => (
                  <tr key={p.id}>
                    <td><strong>{p.nombre}</strong></td>
                    <td>{p.tipo === 'grupo' ? 'Grupo' : 'Cliente'}</td>
                    <td style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.texto}>
                      {(p.texto || '').slice(0, 80)}{(p.texto || '').length > 80 ? '…' : ''}
                    </td>
                    <td>{p.tiene_imagen ? 'Sí' : '-'}</td>
                    <td>{p.updated_at ? formatFecha(p.updated_at) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2 style={{ marginBottom: 12 }}>Historial de envíos</h2>
        <p style={{ color: '#666', marginBottom: 12 }}>
          Mensajes enviados por WhatsApp que se guardaron en historial (al marcar &quot;Guardar en historial de envíos&quot;).
        </p>
        <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <label>
            Cliente ID (opcional)
            <input
              type="number"
              placeholder="ID cliente"
              value={filtroClienteId}
              onChange={e => setFiltroClienteId(e.target.value)}
              style={{ marginLeft: 6, padding: 6, width: 100 }}
            />
          </label>
          <label>
            Límite
            <select value={limite} onChange={e => setLimite(Number(e.target.value))} style={{ marginLeft: 6, padding: 6 }}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </label>
          <button type="button" className="btn-secondary" onClick={loadEnvios}>Actualizar</button>
        </div>
        {loadingEnvios ? (
          <p>Cargando historial...</p>
        ) : envios.length === 0 ? (
          <p>No hay envíos registrados.</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th>CUIT</th>
                  <th>Plantilla</th>
                  <th>Mensaje (inicio)</th>
                </tr>
              </thead>
              <tbody>
                {envios.map(e => (
                  <tr key={e.id}>
                    <td>{formatFecha(e.enviado_at)}</td>
                    <td>{e.razon_social || `ID ${e.cliente_id}`}</td>
                    <td>{e.cuit || '-'}</td>
                    <td>{e.template_nombre || '-'}</td>
                    <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.mensaje_texto}>
                      {(e.mensaje_texto || '').slice(0, 60)}{(e.mensaje_texto || '').length > 60 ? '…' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnviosWhatsApp;
